import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { contactDisplayName, formatDate, firstEmail } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const initial = sp.get("q") || "";
  const [q, setQ] = useState(initial);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const id = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } }).then((r) => setResults(r.data));
      setSp({ q: q.trim() });
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="space-y-4 max-w-4xl" data-testid="search-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Globale Suche</h1>
        <p className="text-sm text-slate-500 mt-1">Über Kontakte, Objekte, Mietverhältnisse, Dokumente und Aufgaben</p>
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suchbegriff eingeben..."
          className="pl-9 h-11"
          data-testid="search-input"
        />
      </div>

      {!results && q && <div className="text-sm text-slate-500">Suche...</div>}
      {results && (
        <div className="space-y-4">
          <Group title="Kontakte" items={results.contacts}
            render={(c) => <Link to={`/kontakte/${c.id}`} className="block" data-testid={`search-contact-${c.id}`}>{contactDisplayName(c)} {firstEmail(c.emails) && <span className="text-xs text-slate-500">· {firstEmail(c.emails)}</span>}</Link>}
          />
          <Group title="Objekte" items={results.properties}
            render={(p) => <Link to={`/objekte/${p.id}`} className="block">{p.name} {p.address?.city && <span className="text-xs text-slate-500">· {p.address.city}</span>}</Link>}
          />
          <Group title="Mietverhältnisse" items={results.tenancies}
            render={(t) => <Link to={`/mietverhaeltnisse/${t.id}`} className="block" data-testid={`search-tenancy-${t.id}`}>{t.contract_number} <span className="text-xs text-slate-500">· {[t.tenant_names, t.property_name, t.unit_label].filter(Boolean).join(" · ")}</span></Link>}
          />
          <Group title="Dokumente" items={results.documents}
            render={(d) => <Link to="/dokumente" className="block">{d.title}</Link>}
          />
          <Group title="Aufgaben" items={results.tasks}
            render={(t) => <Link to="/aufgaben" className="block">{t.title} {t.due_date && <span className="text-xs text-slate-500">· {formatDate(t.due_date)}</span>}</Link>}
          />
        </div>
      )}
    </div>
  );
}

function Group({ title, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-5 py-2.5 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600 flex justify-between">
        <span>{title}</span>
        <span className="text-slate-400">{items.length}</span>
      </div>
      <ul>
        {items.map((it, i) => (
          <li key={it.id || i} className="px-5 py-2 text-sm hover:bg-slate-50 border-t border-slate-100 first:border-0">
            {render(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}
